<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import { onKeyStroke } from '@vueuse/core'
import IconClose from '@/assets/icons/close.svg'
import IconLoading from '@/assets/icons/loading.svg'

const emit = defineEmits(['close'])

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  closeHandler: {
    type: [Function, null],
    default: () => null,
  },
  name: {
    type: String,
    required: true,
  },
  async: {
    type: Boolean,
    default: false,
  },
  bgWhite: {
    type: Boolean,
    default: false,
  },
  showMessage: {
    type: Boolean,
    default: false,
  },
  contentClass: {
    type: String,
    default: '',
  },
  modalClass: {
    type: String,
    default: '',
  },
  modalOverlayClass: {
    type: String,
    default: '',
  },
  buttonCloseClasses: {
    type: String,
    default: '',
  },
})

const { $modal } = useNuxtApp()

const isOpen = computed(() => $modal[props.name])
const loading = ref(false)

const close = () => {
  if (props.closeHandler) {
    props.closeHandler()
  }
  $modal.close(props.name)
  loading.value = props.async
  emit('close')
}

const $attrs = useAttrs() as Record<string, string>

const filteredAttrs = computed(() => {
  const { class: _, 'data-testid': __, ...rest } = $attrs
  return rest
})

onKeyStroke('Escape', close)

const handleLoaded = () => {
  loading.value = false
}

onBeforeMount(() => {
  loading.value = props.async
})
</script>

<template>
  <Teleport to="body">
    <transition
      enter-from-class="opacity-0"
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-500"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        v-bind="filteredAttrs"
        :class="[
          twMerge(
            $attrs.class,
            'fixed left-0 top-0 z-[60] flex h-full w-[100vw] flex-col items-center justify-center bg-black/30 p-4 md:px-16 content-visibility backdrop-blur',
            modalOverlayClass,
          ),
        ]"
        :data-testid="$attrs['data-testid'] ?? `modal-${name.toLowerCase()}`"
        @click.self="close"
      >
        <IconLoading v-show="loading" class="my-28 size-7 text-p-500" />

        <div
          v-show="!loading"
          class=""
          :class="[
            twMerge(
              props.bgWhite ? 'bg-white' : 'bg-s-50',
              'relative rounded-xl bg-s-50 px-4 pt-12 pb-12 shadow-dp-01 overflow-hidden',
              modalClass,
            ),
          ]"
        >
          <button
            :data-testid="`close-${name}-modal-btn`"
            :class="[
              twMerge(
                'bg-white p-1.5 rounded-lg outline-none absolute top-5 right-5 z-10 border border-white hover:border-p-500 hover:bg-p-100 transition-colors duration-200',
                buttonCloseClasses,
              ),
            ]"
            @click.prevent="close"
          >
            <IconClose class="size-5" />
          </button>

          <div :class="[twMerge('h-full px-6', contentClass)]">
            <slot v-if="!showMessage" :on-loaded="handleLoaded" />
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>
